import PaymentRequestService from "../services/PaymentRequestService";
import * as XLSX from "xlsx";
import moment from "moment";
import { DateFormat } from "./filters";
import {
  getParseDateYMDFormate,
  getParseDateYMDFormateTime,
} from "./collection";

export const exportPaymentListToExcel = async (
  payload = {},
  fileName = "payment-export.xlsx",
  isClientSide = false,
  onStart = null,
  onSuccess = null,
  onError = null,
) => {
  try {
    if (typeof onStart === "function") {
      onStart();
    }

    const response =
      await PaymentRequestService.getPaymentRequestExcellList(payload);

    const { data, status } = response;

    console.log("Excel Export Response:", {
      status,
      dataType: typeof data,
      dataSize: data.size || data.length,
    });

    if (status === 200 && data) {
      if (isClientSide && data && data.data) {
        const result = exportPaymentListClientSide(data.data, fileName);
        if (result) {
          if (typeof onSuccess === "function") {
            onSuccess(response);
          }
          return { success: true, data: response };
        } else {
          throw new Error("Client-side export failed");
        }
      }
      // Check if data is already a blob
      let blob;
      if (data instanceof Blob) {
        blob = data;
      } else {
        blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (typeof onSuccess === "function") {
        onSuccess(response);
      }

      return { success: true, data: response };
    } else {
      throw new Error("Failed to export Excel");
    }
  } catch (error) {
    console.error("Excel Export Error:", error);

    if (typeof onError === "function") {
      onError(error);
    }

    return { success: false, error };
  }
};

export const exportPaymentApprovalToExcel = async (
  payload = {},
  fileName = "payment-approval-export.xlsx",
  isClientSide = false,
  onStart = null,
  onSuccess = null,
  onError = null,
) => {
  try {
    if (typeof onStart === "function") {
      onStart();
    }

    const response =
      await PaymentRequestService.getPaymentRequestExcellList(payload);

    const { data, status } = response;

    console.log("Excel Approval Export Response:", {
      status,
      dataType: typeof data,
      dataSize: data.size || data.length,
    });

    if (status === 200 && data) {
      if (isClientSide && data && data.data) {
        const result = exportPaymentListClientSide(data.data, fileName);
        if (result) {
          if (typeof onSuccess === "function") {
            onSuccess(response);
          }
          return { success: true, data: response };
        } else {
          throw new Error("Client-side export failed");
        }
      }
      // Check if data is already a blob
      let blob;
      if (data instanceof Blob) {
        blob = data;
      } else {
        blob = new Blob([data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (typeof onSuccess === "function") {
        onSuccess(response);
      }

      return { success: true, data: response };
    } else {
      throw new Error("Failed to export Excel");
    }
  } catch (error) {
    console.error("Excel Export Error (Approval):", error);

    if (typeof onError === "function") {
      onError(error);
    }

    return { success: false, error };
  }
};

export const exportPaymentListClientSide = (
  dataList = [],
  fileName = "payment-list.xlsx",
  options = {},
) => {
  try {
    const {
      sheetName = "Payments",
      dateFields = [
        "JobDate",
        "InvDate",
        "InvPayDate",
        "CreatedAt",
        "SobDate",
        "DueDate",
        "MBLDate",
        "MBLReleaseDate",
      ],
    } = options;

    // Helper to convert dates
    const toDate = (v) => {
      if (!v || v === "") return "";
      const d = v instanceof Date ? v : new Date(v);
      if (isNaN(d.getTime())) return "";
      // Return date object for Excel to format properly
      return d;
    };

    // Map data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "Job No": item.JobNo || "",
      "Job Raised By": item.JobRaisedBy || "",
      "Job Date": getParseDateYMDFormate(item.JobDate),
      "Job Type": item.MJobType || "",
      Branch: item.Branch || "",
      "Transaction No": item.TransactionNo || "",
      "Expense Type": item.PaymentType || "",
      "Payment Type Str": item.PaymentTypeStr || "",
      "Payment Module Type": item.PaymentModuleType || "",
      "Requested By": item.RequestedBy || "",
      "Created At": getParseDateYMDFormate(item.CreatedAt),
      "Payment Status": item.IsPaymentDone || "",
      "Inv Pay Status": item.InvPayStatus || "",
      "Is Email Sent": item.IsEmailSent || "",
      "Inv No": item.InvNo || "",
      "Inv Amt": item.InvAmt || "",
      "Inv Type": item.InvType || "",
      "Inv Category": item.InvCategory || "",
      "Inv Date": getParseDateYMDFormate(item.InvDate),
      "Inv Pay Mode": item.InvPayMode || "",
      "Inv Pay Ref No": item.InvPayRefNo || "",
      "Inv Pay Amt": item.InvPayAmt || "",
      "Inv Pay Date": getParseDateYMDFormate(item.InvPayDate),
      "Inv Pay Remark": item.InvPayRemark || "",
      "Bill To Shipper": item.ScCategory || "",
      "Selling Amt": item.SellingAmt || "",
      "Sc Remark": item.ScRemark || "",
      "SOB Date": getParseDateYMDFormate(item.SobDate),
      "Due Date": getParseDateYMDFormate(item.DueDate),
      "Vessel Voyage": item.VesselVoyage || "",
      MBL: item.MBL || "",
      "MBL Date": getParseDateYMDFormate(item.MBLDate),
      "MBL Release Date": getParseDateYMDFormate(item.MBLReleaseDate),
      HBL: item.HBL || "",
      "SOB Detected": item.SOBDetected || "",
      "Is Final Sales Billing Complete": item.IsFinalSalesBillingComplete || "",
      "Sline Name": item.SlineName || "",
      "Client Name": item.ClientName || "",
      "Imp Client Name": item.ImpClientName || "",
      "Exp Client Name": item.ExpClientName || "",
      POR: item.POR || "",
      POL: item.POL || "",
      POD: item.POD || "",
      FPOD: item.FPOD || "",
      Pickup: item.Pickup || "",
      Handover: item.Handover || "",
      Stuff: item.Stuff || "",
      "Created By": item.createdBy || "",
      "Updated By": item.updatedBy || "",
      "Approval Status":
        item.approvals && item.approvals.length > 0
          ? item.approvals[item.approvals.length - 1].approvalStatus || ""
          : "",
      "Approval Remark":
        item.approvals && item.approvals.length > 0
          ? item.approvals[item.approvals.length - 1].approvalRemark || ""
          : "",
      "Approval Requested At":
        item.approvals && item.approvals.length > 0
          ? getParseDateYMDFormate(
              item.approvals[item.approvals.length - 1].approvalRequestedAt,
            )
          : "",
      "Approval Completed At":
        item.approvals && item.approvals.length > 0
          ? getParseDateYMDFormate(
              item.approvals[item.approvals.length - 1].approvalCompletedAt,
            )
          : "",
      // 'Approval Requested At': item.approvals && item.approvals.length > 0 ? (getParseDateYMDFormateTime(item.approvals[item.approvals.length - 1].approvalRequestedAt).date || '') : '',
      // 'Approval Requested Time': item.approvals && item.approvals.length > 0 ? (getParseDateYMDFormateTime(item.approvals[item.approvals.length - 1].approvalRequestedAt).time || '') : '',
      // 'Approval Completed At': item.approvals && item.approvals.length > 0 ? (getParseDateYMDFormateTime(item.approvals[item.approvals.length - 1].approvalCompletedAt).date || '') : '',
      // 'Approval Completed Time': item.approvals && item.approvals.length > 0 ? (getParseDateYMDFormateTime(item.approvals[item.approvals.length - 1].approvalCompletedAt).time || '') : '',
      "Approval By":
        item.approvals && item.approvals.length > 0
          ? item.approvals[item.approvals.length - 1].approvalBy || ""
          : "",
      "Request By Name":
        item.approvals && item.approvals.length > 0
          ? item.approvals[item.approvals.length - 1].requestByName || ""
          : "",
      "Approval Email Sent":
        item.approvals && item.approvals.length > 0
          ? item.approvals[item.approvals.length - 1].approvalEmailSent
            ? "Yes"
            : "No"
          : "",
    }));
    console.log("excelData=>", excelData);

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Apply date formatting
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const headerMap = {};

      // Build header map
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: range.s.r, c: C });
        const cell = ws[addr];
        if (cell && cell.v) headerMap[String(cell.v)] = C;
      }

      // Format date columns
      // const dateColumnNames = ['Job Date', 'Created At', 'Inv Date', 'Inv Pay Date', 'SOB Date', 'Due Date', 'MBL Date', 'MBL Release Date', 'Approval Requested At', 'Approval Completed At']
      // dateColumnNames.forEach(h => {
      //     const C = headerMap[h]
      //     if (typeof C === 'number') {
      //         for (let R = range.s.r + 1; R <= range.e.r; ++R) {
      //             const addr = XLSX.utils.encode_cell({ r: R, c: C })
      //             const cell = ws[addr]
      //             if (!cell || !cell.v) continue

      //             // If it's already a Date object
      //             if (cell.v instanceof Date) {
      //                 cell.t = 'd'
      //                 cell.z = 'dd-mm-yyyy'
      //             }
      //             // If it's a string or number that can be converted to date
      //             else if (typeof cell.v === 'string' || typeof cell.v === 'number') {
      //                 const d = new Date(cell.v)
      //                 if (!isNaN(d.getTime())) {
      //                     cell.v = d
      //                     cell.t = 'd'
      //                     cell.z = 'dd-mm-yyyy'
      //                 }
      //             }
      //         }
      //     }
      // })
    }

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Excel export error:", error);
    return false;
  }
};

export const genericExcelExport = async (
  apiMethod,
  payload = {},
  fileName = "export.xlsx",
  onStart = null,
  onSuccess = null,
  onError = null,
) => {
  try {
    if (typeof onStart === "function") {
      onStart();
    }

    const response = await apiMethod(payload);
    const { data, status } = response;

    if (status === 200 && data) {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (typeof onSuccess === "function") {
        onSuccess(response);
      }

      return { success: true, data: response };
    } else {
      throw new Error("Failed to export Excel");
    }
  } catch (error) {
    console.error("Excel Export Error:", error);

    if (typeof onError === "function") {
      onError(error);
    }

    return { success: false, error };
  }
};

export const exportGRNListToExcel = (
  dataList = [],
  fileName = "grn-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "GRN Report" } = options;

    // Map GRN data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: item.SN || idx + 1,
      "GRN No": item.GrnNo || "",
      "GRN Counter": item.GrnCounter || "",
      "GRN Date": getParseDateYMDFormate(item.GrnDate),
      "GRN Type": item.GrnType || "",
      "Skip GRN": item.SkipGRN || "",
      "GRN Status": item.GrnStatus || "",
      "Transport Type": item.TransportType || "",
      "Job No": item.JobNo || "",
      "Job Date": getParseDateYMDFormate(item.JobDate),
      "Booking No": item.BookingNo || "",
      "Raised From": item.RaisedFrom || "",
      "Raised By": item.RaisedBy || "",
      "Inv Series": item.InvSeries || "",
      "Inv Date": getParseDateYMDFormate(item.InvDate),
      "Inv Status": item.InvStatus || "",
      "Billing Amt": item.BillingAmt || "",
      Pick: item.Pick || "",
      Hand: item.Hand || "",
      Stuff: item.Stuff || "",
      POR: item.Por || "",
      POL: item.Pol || "",
      POD: item.Pod || "",
      FPOD: item.Fpod || "",
      Client: item.Client || "",
      Sline: item.Sline || "",
      "Container No": item.ContainerNo || "",
      "Container Type": item.ContainerType || "",
      "Vehicle No": item.VehicleNo || "",
      "Driver Name": item.DriverName || "",
      "Driver Contact": item.DriverContact || "",
      "Driver Emp Code": item.DriverEmpCode || "",
      "Vehicle Release Date": getParseDateYMDFormate(item.VehicleReleaseDate),
      "Vehicle Dispatch Date": getParseDateYMDFormate(item.VehicleDispatchDate),
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side GRN Excel export error:", error);
    return false;
  }
};

export const exportJobListToExcel = (
  dataList = [],
  fileName = "job-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Job Report" } = options;

    // Map GRN data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: item.SN || idx + 1,
      Mode: item.Mode || "",
      "Transport Type": item.TransportType || "",
      "Job No": item.JobNo || "",
      "Job Type": item.JobType || "",
      "Job Date": getParseDateYMDFormate(item.JobDate),
      "Raised From": item.RaisedFrom || "",
      "Raised By": item.RaisedBy || "",
      "Mbl No": item.MblNo || "",
      "Hbl No": item.HblNo || "",
      "Client Name": item.ClientName || "",
      "Shipping Line": item.ShippingLine || "",
      "Booking No": item.BookingNo || "",
      "Stuffing Type": item.StuffingType || "",
      "FCL LCL": item.FCL_LCL || "",
      Containers: item.FreightContainers || "",
      "Container Type": item.FreightContainersType || "",
      "Container Count": item.MultipleContainerNo || "",
      SINVNO: item.SINVNO || "",
      SBILLNO: item.SBILLNO || "",
      "Vessel Voyage": item.VesselVoyage || "",
      Por: item.Por || "",
      Pol: item.Pol || "",
      Pod: item.Pod || "",
      Fpod: item.Fpod || "",
      "Bl Type": item.blType || "",
      "Frght Terms Hbl": item.frghtTermsHbl || "",
      "Frght Terms Liner": item.frghtTermsLiner || "",
      "Frght Terms Mbl": item.frghtTermsMbl || "",
      "Final Billing Completed Status": item.FinalBillingCompletedStatus || "",
      "Prealert Status": item.PrealertStatus || "",
      "Is Manifestation": item.IsManifestation || "",
      "Sob Status": item.SobStatus || "",
      "Is Billing Type Status": item.IsBillingTypeStatus || "",
      "Line Inv Status": item.LineInvStatus || "",
      "Billing Status": item.BillingStatus || "",
      "Tally Billing Series": item.TallyBillingSeries || "",
      "Tally Inv No": item.TallyInvNo || "",
      "Tally Inv Type": item.TallyInvType || "",
      "Tally Inv Date": item.TallyInvDate || "",
      "Tally Exp No": item.TallyExpNo || "",
      "Tally Exp Type": item.TallyExpType || "",
      "Tally Exp Date": item.TallyExpDate || "",
      "Of First Payment Date":
        getParseDateYMDFormate(item.OfFirstPaymentDate) || "",
      "Sline First Pay Date":
        getParseDateYMDFormate(item.SlineFirstPayDate) || "",
      "Tpt Invoice Closed": (item.tptInvoiceClosed == 1 ? "Yes" : "No") || "",
      "FF Invoice Closed": (item.ffInvoiceClosed == 1 ? "Yes" : "No") || "",
      "CC Invoice Closed": (item.ccInvoiceClosed == 1 ? "Yes" : "No") || "",
      "MISC Invoice Closed": (item.miscInvoiceClosed == 1 ? "Yes" : "No") || "",
      "Is Consolidation": (item.isConsolidation == 1 ? "Yes" : "No") || "",
      "Closed Overseas Status": item.closedOverseasStatus || "",
      "Billing Type": item.billingType || "",
      POR: item.POR || "",
      POL: item.POL || "",
      POD: item.POD || "",
      FPOD: item.FPOD || "",
      "Sys SOB": getParseDateYMDFormate(item.SysSOB),
      "Sys ETA Estimated": getParseDateYMDFormate(item.SysETA_estimated),
      SOB: getParseDateYMDFormate(item.SOB),
      "Mbl Date": getParseDateYMDFormate(item.MblDate),
      "Mbl Release Date": getParseDateYMDFormate(item.MblReleaseDate),
      "Handover Actual Date": getParseDateYMDFormate(item.HandoverDateActual),
      "Vgm Submission Actual Date": getParseDateYMDFormate(
        item.VgmSubmissionDateActual,
      ),
      "Si Submission Actual Date": getParseDateYMDFormate(
        item.SiSubmissiondateActual,
      ),
      "Tr Submission Actual Date": getParseDateYMDFormate(
        item.TrSubmissiondateActual,
      ),
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side GRN Excel export error:", error);
    return false;
  }
};

export const exportOFRateJobListToExcel = (
  dataList = [],
  fileName = "Of_rate_job-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "OF Rate Job Report" } = options;

    // Map GRN data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: item.SN || idx + 1,
      JobNo: item.JobNo || "",
      "Job Type": item.JobType || "",
      "Job Date": getParseDateYMDFormate(item.JobDate),
      Sob: getParseDateYMDFormate(item.Sob),
      "Eta Estimated": getParseDateYMDFormate(item.EtaEstimated),
      "Eta Actual": getParseDateYMDFormate(item.EtaActual),
      "Mbl Release Date": getParseDateYMDFormate(item.MblReleaseDate),
      "Mbl Date": getParseDateYMDFormate(item.MblDate),
      EtaActual: getParseDateYMDFormate(item.EtaActual),
      EtaActual: getParseDateYMDFormate(item.EtaActual),
      "Transport Type": item.TransportType || "",
      "Ass Container Type": item.MultiContainerType || "",
      "Container Nos": item.ContainerNos || "",
      "Container Count": item.ContainerCount || "",
      "Stuffing Type": item.StuffingType || "",
      Vessel: item.leg1_vessel || "",
      Voyage: item.leg1_voyage || "",
      "Mbl No": item.MblNo || "",
      "Hbl No": item.HblNo || "",
      blType: item.blType || "",
      frghtTermsHbl: item.frghtTermsHbl || "",
      frghtTermsMbl: item.frghtTermsMbl || "",
      frghtTermsLiner: item.frghtTermsLiner || "",
      "Raised From": item.RaisedFrom || "",
      "Raised By": item.RaisedBy || "",
      "Client Name": item.Client || "",
      "Imp Client": item.ImpClient || "",
      "Exp Client": item.ExpClient || "",
      "Shipping Line": item.Sline || "",
      "Enq No": item.EnqNo || "",
      "Enq Date": getParseDateYMDFormate(item.EnqDate),
      "Final BillingS tatus": item.FinalBillingStatus || "",
      "Tpt Invoice Closed":
        (typeof item.TptInvoiceClosed === "string"
          ? item.TptInvoiceClosed
          : item.TptInvoiceClosed == 1
            ? "Yes"
            : "No") || "",
      "FF Invoice Closed":
        (typeof item.CfInvoiceClosed === "string"
          ? item.CfInvoiceClosed
          : item.CfInvoiceClosed == 1
            ? "Yes"
            : "No") || "",
      "CC Invoice Closed":
        (typeof item.CcInvoiceClosed === "string"
          ? item.CcInvoiceClosed
          : item.CcInvoiceClosed == 1
            ? "Yes"
            : "No") || "",
      Pick: item.Pick || "",
      Hand: item.Hand || "",
      Stuff: item.Stuff || "",
      Por: item.Por || "",
      Pol: item.Pol || "",
      Pod: item.Pod || "",
      Fpod: item.Fpod || "",
      "Quot Particular": item.QuotParticular || "",
      "Quot Buying Rate": item.QuotBuyingRate || "",
      "Quot Selling Rate": item.QuotSellingRate || "",
      "Container Type": item.ContainerType || "",
      "Container Buying Rate":
        item.containerBuyRate + " " + item.Currency || "",
      "Container Selling Rate":
        item.containerSellRate + " " + item.Currency || "",
      "Total Container": item.totalContainer || "",
      TUES: item.TUES || "",
      "Exp Ledger": item.expenseLedgerGroup || "",
      "Exp Inv Type": item.expenseInvoiceType || "",
      "Exp Inv No": item.expenseInvoiceNo || "",
      "Exp Inv Date": getParseDateYMDFormate(item.expenseInvoiceDate) || "",
      "Exp Seller": item.expenseSeller || "",
      "Exp Particular": item.expenseDescription || "",
      "Exp Amt": item.expenseAmount || "",

      "Rev Ledger": item.incomeLedgerGroup || "",
      "Rev Inv Type": item.incomeInvoiceType || "",
      "Rev Inv No": item.incomeInvoiceNo || "",
      "Rev Inv Date": getParseDateYMDFormate(item.incomeInvoiceDate) || "",
      "Rev Seller": item.incomeSeller || "",
      "Rev Particular": item.incomeDescription || "",
      "Rev Amt": item.incomeAmount || "",

      "Concat Expense Descriptions": item.concatenatedExpenseDescriptions || "",
      "Concat Expense Inv": item.concatenatedExpenseInvoiceNos || "",
      "Expense Final": item.sumExpenseValDtlsTotInvVal || "",
      "Concat Invoice Descriptions": item.concatenatedIncomeDescriptions || "",
      "Concat Invoice Inv": item.concatenatedIncomeInvoiceNos || "",
      "Invoice Final": item.sumIncomeValDtlsTotInvVal || "",

      Profit: item.profit || "",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Rate Excel export error:", error);
    return false;
  }
};

export const exportAssignmentListToExcel = (
  dataList = [],
  fileName = "assignment-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Assignment Report" } = options;

    // Map Assignment data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "Assignment No": (item.assignSeries || "").split(",")[0] || "",
      "Execution No": item.salesSeries || "",
      "Client Name": item.clientName || "",
      "Pickup Location": item.PICK || "",
      "Factory Location": item.STUFF || "",
      "Handover Location": item.HAND || "",
      "Booking No": item.bookingNo || "",
      "Container No": item.ContainerNo || "",
      "Vehicle Required": item.noOfTrucks || "",
      "Booked Vehicle": item.vehicleNO || "",
      "No of GRN Generated": item.GRNCount || "",
      "GRN Series": item.GRNSeries || "",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Assignment Excel export error:", error);
    return false;
  }
};

export const exportProfitLossListToExcel = (
  dataList = [],
  fileName = "profit-loss-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Profit Loss Report" } = options;

    // Helper to return Date object for Excel
    const toExcelDate = (date) => {
      if (!date || date === "") return "";
      const d = moment(date, [
        "YYYY-MM-DD",
        "DD-MM-YYYY",
        "YYYY-MM-DD HH:mm:ss",
        "DD-MM-YYYY HH:mm:ss",
        moment.ISO_8601,
      ]);
      return d.isValid() ? d.toDate() : date;
    };

    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "Job No": item.SalesSeries || item.JobNo || "",
      "Job Date": toExcelDate(item.JobDate || item.JOBDate),
      "Job Type": item.JobType || item.JOBType || "",
      "Shipment Type": item.shipmentTypeStatus || "",
      "Transport Type": item.transportType || "",
      "Raised By": item.RaisedBy || "",

      Branch: item.BranchName || "",
      Client: item.BillingParty || "",
      Exporter: item.exporterName || "",
      Importer: item.importerName || "",
      "Shipping Line": item.shippingLine || "",
      "Billing Type": item.billingType || "",
      "BL Type": item.blType || "",
      "Freight Terms HBL": item.frghtTermsHbl || "",
      "Freight Terms MBL": item.frghtTermsMbl || "",
      "Freight Terms Liner": item.frghtTermsLiner || "",
      "Vessel Voyage": item.vessel_voyage || "",
      HBL: item.HBLNo || "",
      MBL: item.MBLNo || "",
      MBLDate: toExcelDate(item.MblDate || item.MBLDate),
      MBLReleaseDate: toExcelDate(item.MblReleaseDate || item.MBLReleaseDate),
      EtaEstimated: toExcelDate(item.EtaEstimated || item.SysETA_estimated),
      EtaActual: toExcelDate(item.EtaActual || item.SysETA_actual),
      Sob: toExcelDate(item.Sob || item.SOB),
      "Container Count": item.ContainerCount || "",
      "Container Info": item.containerInfo || "",
      "Container Type": item.ContainerTypes || "",
      "Shipping Line": item.shippingLine || "",
      "From Date": toExcelDate(item.fromDate || item.FromDate),
      "To Date": toExcelDate(item.toDate || item.ToDate),
      "Expense Final": item.ExpenseFinal || 0,
      "Invoice Final": item.InvoiceFinal || 0,
      "Quot Buying Rate": item.QuotBuyingRate || 0,
      "Quot Selling Rate": item.QuotSellingRate || 0,
      "Container Buying Rate": item.ContainerBuyingRate || 0,
      "Container Selling Rate": item.ContainerSellingRate || 0,
      Expenses: item.Expenses || 0,
      Incomes: item.Incomes || 0,
      Profit: item.Profit || 0,
      "Profit %": item.ProfitPercent || 0,
      totPLLabel: item.totPLLabel || "",
      Mismatch: item.IsMismatch ? "Yes" : "No",
      Status: item.ApprovalStatus || "",
      "Lock Status": item.LockStatus || "",
      "Billing Status": item.BillStatus || "",
      "FF Invoice Closed": item.FfInvoiceClosed || "",
      "CC Invoice Closed": item.CcInvoiceClosed || "",
      "TPT Invoice Closed": item.TptInvoiceClosed || "",
      "Closed Overseas": item.closedOverseasStatus || "",
      "Created By": item.CreatedBy || "",
      "Created Time": toExcelDate(item.CreatedTime),
      "Updated Time": toExcelDate(item.updatedAt),
      Remarks: item.Remarks || "",
      Description: item.Description || "",
      POR: item.POR || "",
      POL: item.POL || "",
      POD: item.POD || "",
      FPOD: item.FPOD || "",
      Pick: item.PICK || "",
      Stuff: item.STUFF || "",
      Hand: item.HAND || "",
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Apply date formatting for Excel to recognize as dates for filtering
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const headerMap = {};

      // Build header map
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const addr = XLSX.utils.encode_cell({ r: range.s.r, c: C });
        const cell = ws[addr];
        if (cell && cell.v) headerMap[String(cell.v)] = C;
      }

      const dateColumnNames = [
        "Job Date",
        "MBLDate",
        "MBLReleaseDate",
        "EtaEstimated",
        "EtaActual",
        "Sob",
        "From Date",
        "To Date",
        "Created Time",
        "Updated Time",
      ];

      dateColumnNames.forEach((h) => {
        const C = headerMap[h];
        if (typeof C === "number") {
          for (let R = range.s.r + 1; R <= range.e.r; ++R) {
            const addr = XLSX.utils.encode_cell({ r: R, c: C });
            const cell = ws[addr];
            if (!cell || !cell.v) continue;

            if (cell.v instanceof Date) {
              cell.t = "d";
              cell.z = "dd-mm-yyyy";
            }
          }
        }
      });
    }

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Profit Loss Excel export error:", error);
    return false;
  }
};

export const exportProfitLossLogToExcel = (
  dataList = [],
  fileName = "profit-loss-log.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Profit Loss Logs" } = options;

    // Map Profit Loss Log data to Excel structure
    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "Field Changed": item.pl_field_changed || "",
      "Old Value": item.pl_old_value || "",
      "New Value": item.pl_new_value || "",
      "Changed By": item.pl_log_cu_relation
        ? item.pl_log_cu_relation.employeeName
        : "-",
      "Date & Time":
        getParseDateYMDFormateTime(item.pl_log_created_at).date +
        " " +
        getParseDateYMDFormateTime(item.pl_log_created_at).time,
    }));

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Create workbook and append sheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Write file
    // Append dynamic timestamp to filename
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);

    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Profit Loss Log Excel export error:", error);
    return false;
  }
};

export const exportCreditDebitNoteListToExcel = (
  dataList = [],
  fileName = "credit-debit-note-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Credit Debit Note Report" } = options;

    const excelData = dataList.map((item, idx) => ({
      "SN.": idx + 1,
      "Assignment Type": item.TransportType || "",
      "Assignment Mode":
        item.ShipmentType === "1"
          ? "Sea"
          : item.ShipmentType === "2"
            ? "Air"
            : item.ShipmentType || "",
      "Branch Name": item.BranchName || "",
      "Invoice No.": item.NoteSeries || "",
      "Invoice Date": getParseDateYMDFormate(item.NoteDate) || "",
      "Invoice Type": item.NoteType || "",
      To: item.To || "",
      "Overseas Agent": item.Overseas_agent || "",
      Shipper: item.Shipper || "",
      Consignee: item.Consignee || "",
      SOB: getParseDateYMDFormate(item.SobDate) || "",
      "ETA Destination":
        getParseDateYMDFormate(item.EtaDestEst) ||
        getParseDateYMDFormate(item.ETADest) ||
        "",
      MBL: item.MblNo || item.MasterorLinerBlNo || "",
      HBL: item.HblNo || item.BLNumber || "",
      Destination: item.Fpod || item.FinalPlace || "",
      "Booking No": item.ValidBookingNo || "",
      "Cont No.": item.containerNoArryNote || "",
      "Cont Type.": item.ContainerTypesArr || "",
      "SHIPPER INV. DETAILS.": item.ShipperInvDet || "",
      "Assignment No": item.JobNo || item.SalesSeries || "",
      "Assignment Date":
        item.JobDate || getParseDateYMDFormate(item.NoteCreatedTime) || "",
      "Client Name": item.Client || item.clientName || "",
      "Shipping Line": item.Sline || item.shippingLine || "",
      "Invoice Amount": item.NoteAmount || 0,

      "Paid Amount": item.paidAmount || 0,
      Currency: item.payCurrency || "",
      "Payment Date": getParseDateYMDFormate(item.payDate) || "",
      "Payment Mode": item.payMode || "",
      "Payment Reference No": item.payRef || "",
      "Payment Status":
        item.payStatus == 1 ? "Paid" : item.payStatus == 0 ? "Pending" : "",

      "BL Type": item.BLType || "",
      "Prealert Status": item.PrealertStatus || "",
      "Prealert Date": getParseDateYMDFormate(item.PrealertDate) || "",
      "Manifestation Status": item.ManifestStatus || "",
      "Manifestation Date": getParseDateYMDFormate(item.ManifestDate) || "",
      Remark: item.payRemark || "",
      "Updated By": "",
      "Updated Date": "",
    }));

    // Append dynamic timestamp to filename
    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Credit Debit Note Excel export error:", error);
    return false;
  }
};

export const exportAffectedContainersToExcel = (
  dataList = [],
  fileName = "affected-containers-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "Affected Containers" } = options;

    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "ID Date": item.id_date || "",
      "JOB NO": item.JOBNO || "",
      "JOB Date": item.JOBDate || "",
      "Client Name": item.ClientName || "",
      "Branch Name": item.BranchName || "",
      "Container No": item.container_number || "",
      "Booking No": item.booking_number || "",
      Carrier: item.carrier_name || "",
      Status: item.status_verbose || "",
      "POD Discharge Prediction Created":
        item.pod_discharge_prediction && item.pod_discharge_prediction.created
          ? DateFormat(item.pod_discharge_prediction.created)
          : "",
      "POD Discharge Prediction Predicted":
        item.pod_discharge_prediction && item.pod_discharge_prediction.predicted
          ? DateFormat(item.pod_discharge_prediction.predicted)
          : "",
      "POD Predicted Date Lower":
        item.pod_timeofarrival_warning &&
        item.pod_timeofarrival_warning.predicted_date_lower
          ? item.pod_timeofarrival_warning.predicted_date_lower
          : "",
      "POD Predicted Date Upper":
        item.pod_timeofarrival_warning &&
        item.pod_timeofarrival_warning.predicted_date_upper
          ? item.pod_timeofarrival_warning.predicted_date_upper
          : "",
      POL: item.pol_loc ? item.pol_loc.locode : "",
      TSP1: item.tsp1_loc ? item.tsp1_loc.locode : "",
      POD: item.pod_loc ? item.pod_loc.locode : "",
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side Affected Containers Excel export error:", error);
    return false;
  }
};

export default {
  exportPaymentListToExcel,
  exportPaymentApprovalToExcel,
  exportPaymentListClientSide,
  genericExcelExport,
  exportGRNListToExcel,
  exportAssignmentListToExcel,
  exportProfitLossListToExcel,
  exportProfitLossLogToExcel,
  exportCreditDebitNoteListToExcel,
  exportAffectedContainersToExcel,
};
export const exportSlineApprovalListToExcel = (
  dataList = [],
  fileName = "sline-approval-list.xlsx",
  options = {},
) => {
  try {
    const { sheetName = "SLine Approval Report" } = options;

    const toDate = (v) => {
      if (!v || v === "" || v === "NA") return "";
      const parts = v.split("-");
      if (parts.length === 3) {
        // Handle DD-MM-YYYY
        const d = new Date(parts[2], parts[1] - 1, parts[0]);
        return isNaN(d.getTime()) ? v : d;
      }
      const d = new Date(v);
      return isNaN(d.getTime()) ? v : d;
    };

    const excelData = dataList.map((item, idx) => ({
      SN: idx + 1,
      "Job No": item.jobNo || "",
      "Job Date": toDate(item.jobDate),
      "Job Type": item.rsa_job_type || "",
      "Transport Type": item.rsa_transport_type || "",
      Mode: item.mode || "",
      Vessel: item.vessel || "",
      Voyage: item.voyage || "",
      "MBL No": item.mblNo || "",
      "HBL No": item.hblNo || "",
      "SOB Date": toDate(item.sobDate),
      "MBL Date": toDate(item.mblDate),
      "MBL Rel Date": toDate(item.mblReleaseDate),
      POR: item.por || "",
      POL: item.pol || "",
      POD: item.pod || "",
      FPOD: item.fpod || "",
      Client: item.clientName || "",
      "Shipping Line": item.shippingLine || "",
      "Inv No": item.invoice_details.invNo || "",
      "Inv Date": toDate(item.invoice_details.invDate),
      "Pay Req Date": toDate(item.invoice_details.paymentReqDate),
      "Inv Category": item.invoice_details.invCategory || "",
      "Inv Type": item.invoice_details.invType || "",
      "Inv Amount": item.invoice_details.billingAmount || "",
      "Resolved Amount": item.invoice_details.resolvedAmount || "",
      "Dispute Reason": item.invoice_details.disputeReason || "",
      "Dispute Status": item.invoice_details.status || "",
      "To be Billed": item.invoice_details.category || "",
      "Resolution Remark": item.invoice_details.remark || "",
      "Approval Status": item.rsa_approval_status || "",
      "Requested By": item.requesterName || "",
      "Requested At": toDate(item.rsa_approval_requested_at_fmt),
      Approver: item.approverName || "",
      "Approved At": toDate(item.rsa_approval_completed_at_fmt),
    }));

    const ws = XLSX.utils.json_to_sheet(excelData);

    // Apply date formatting for Excel filters to work
    if (ws["!ref"]) {
      const range = XLSX.utils.decode_range(ws["!ref"]);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        for (let R = range.s.r + 1; R <= range.e.r; ++R) {
          const addr = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[addr];
          if (cell && cell.v instanceof Date) {
            cell.t = "d";
            cell.z = "dd-mm-yyyy";
          }
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    const timestamp = new Date()
      .toISOString()
      .replace(/[-:T.]/g, "")
      .slice(0, 14);
    const finalFileName = fileName.replace(".xlsx", `_${timestamp}.xlsx`);
    XLSX.writeFile(wb, finalFileName);

    return true;
  } catch (error) {
    console.error("Client-side SLine Approval Excel export error:", error);
    return false;
  }
};



// WEBPACK FOOTER //
// ./src/helper/ExcelExportHelper.js